import { NoteTemplateEditor } from './NoteTemplateEditor';
import { StateWorksheetConfigEditor } from './StateWorksheetConfigEditor';
import { PostpartumCourseConfigEditor } from './PostpartumCourseConfigEditor';
import { NewbornGrowthConfigEditor } from './NewbornGrowthConfigEditor';
import { validateNewbornGrowthConfiguration } from './newbornGrowth';
import { validateBirthConfiguration } from './birthRules';
import { validateNewbornScreeningConfiguration } from './newbornScreening';
import { NewbornScreeningConfigEditor } from './NewbornScreeningConfigEditor';
import { BirthTimerConfigEditor } from './BirthTimerConfigEditor';
import { BirthRuleConfigEditor } from './BirthRuleConfigEditor';
import { BirthDischargeConfigEditor } from './BirthDischargeConfigEditor';
import { RulePreview } from './RulePreview';
import { CustomObservationEditor, ColumnLabelEditor } from './CustomObservationEditor';
import { customDefinitionIssues, withCustomColumns } from './customDefinitions';
import { EventPolicyEditor } from './EventPolicyEditor';
import { ClinicalReferenceEditor } from './ClinicalReferenceEditor';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { eventCatalog } from './catalog.generated';
import { prenatalDefinition } from './definitions';
import type { PracticeConfiguration, PracticeRule } from './rules';

export type ConfigurationPublication = Omit<PracticeConfiguration, 'version'> & { expected_version: number };
/** Clinical edits become a new assessment; unrelated layout edits preserve completed items. */
export function preserveChecklistSemantics(draft: PracticeConfiguration, baseline: PracticeConfiguration) {
  return draft.checklist.map((item) => {
    const previous = baseline.checklist.find((candidate) => candidate.id === item.id);
    const signature = (value: typeof item) =>
      JSON.stringify([
        value.label.trim(),
        value.min_ga_days,
        value.max_ga_days,
        value.consent_test?.trim() || null,
        value.handout_url?.trim() || null,
      ]);
    return previous && signature(previous) !== signature(item) ? { ...item, id: crypto.randomUUID() } : item;
  });
}
const sourceNames: Record<string, string> = {
  'VITALS.BP:systolic': 'Systolic blood pressure (mmHg)',
  'VITALS.BP:diastolic': 'Diastolic blood pressure (mmHg)',
  'VITALS.PULSE:rate': 'Maternal pulse (beats/min)',
  'VITALS.TEMP:value_c': 'Temperature (°C)',
  'VITALS.RR:rate': 'Respiratory rate (breaths/min)',
  'VITALS.SPO2:value_pct': 'Oxygen saturation (%)',
  'VITALS.WEIGHT:grams': 'Maternal weight (grams)',
  'VITALS.HEIGHT:cm': 'Height (cm)',
  'PRENATAL.FUNDAL_HEIGHT:cm': 'Fundal height (cm)',
  'PRENATAL.FUNDAL_HEIGHT:fingers': 'Fundal height (fingerbreadths)',
  'PRENATAL.FHT:bpm': 'Fetal heart rate (beats/min)',
  'PRENATAL.FHT:low_bpm': 'Fetal heart rate, range low (beats/min)',
  'PRENATAL.FHT:high_bpm': 'Fetal heart rate, range high (beats/min)',
};
const numericSources = Object.entries(eventCatalog).flatMap(([type, entry]) =>
  /^(VITALS|PRENATAL)\./.test(type)
    ? Object.entries(entry.schema.properties)
        .filter(([, spec]) => ['integer', 'number'].includes(spec.type))
        .map(([path]) => ({ value: `${type}:${path}`, label: sourceNames[`${type}:${path}`] ?? `${type} · ${path}` }))
    : [],
);
function Choice({
  label,
  value,
  choices,
  onChange,
}: {
  label: string;
  value: string;
  choices: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label} className="min-h-11">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {choices.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  );
}
const choices = (values: string[]) => values.map((value) => ({ value, label: value }));

export function PracticeConfigurationEditor({
  configuration,
  publish,
}: {
  configuration: PracticeConfiguration;
  publish: (input: ConfigurationPublication) => Promise<PracticeConfiguration>;
}) {
  const [draft, setDraft] = useState(() => structuredClone(configuration));
  const [baseline, setBaseline] = useState(() => structuredClone(configuration));
  const [reviewed, setReviewed] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  function update(patch: Partial<PracticeConfiguration>) {
    setDraft((current) => ({ ...current, ...patch }));
    setReviewed(false);
    setMessage('');
  }
  function rule(index: number, patch: Partial<PracticeRule>) {
    update({ rules: draft.rules.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  }
  const displayedDefinition = withCustomColumns(
    prenatalDefinition,
    draft.custom_observations ?? [],
    draft.column_labels ?? {},
  );
  const columns = [...new Set([...draft.column_order, ...displayedDefinition.columns.map((c) => c.key)])];
  function move(index: number, direction: number) {
    const next = [...columns];
    [next[index], next[index + direction]] = [next[index + direction], next[index]];
    update({ column_order: next });
  }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!reviewed || !reviewNote.trim() || busy) return;
    const customIssues = [
      ...customDefinitionIssues(draft.custom_observations ?? []),
      ...(draft.newborn_growth ? validateNewbornGrowthConfiguration(draft.newborn_growth) : []),
      ...(draft.birth_rules ? validateBirthConfiguration(draft.birth_rules) : []),
      ...(draft.newborn_screening ? validateNewbornScreeningConfiguration(draft.newborn_screening) : []),
    ];
    if (customIssues.length) {
      setMessage(customIssues.join(' '));
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const { version, ...content } = draft;
      const result = await publish({
        ...content,
        checklist: preserveChecklistSemantics(draft, baseline),
        expected_version: version,
        clinically_reviewed: true,
        review_note: reviewNote.trim(),
      });
      setDraft(structuredClone(result));
      setBaseline(structuredClone(result));
      setReviewed(false);
      setReviewNote('');
      setMessage(`Published practice configuration version ${result.version}.`);
    } catch {
      setMessage(
        'Could not publish. Your draft is retained. If another editor published changes, reload the latest version and reconcile before retrying.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <details>
      <summary>Practice flowsheet configuration</summary>
      <form onSubmit={(event) => void save(event)} className="flex flex-col gap-6 pt-4">
        <Alert>
          <AlertDescription>
            Changes apply across this practice. Review sources, units and clinical wording before activation. No
            thresholds are supplied automatically.
          </AlertDescription>
        </Alert>
        <p>
          Editing version {draft.version}. Latest loaded version: {configuration.version}.
        </p>
        {configuration.version > draft.version && (
          <p role="alert">A newer version is available. Your draft has not been replaced.</p>
        )}
        <fieldset disabled={busy} className="flex flex-col gap-6">
          <NoteTemplateEditor
            value={draft.note_templates ?? []}
            onChange={(note_templates) => update({ note_templates })}
          />
          <StateWorksheetConfigEditor
            value={draft.state_worksheets ?? []}
            onChange={(state_worksheets) => update({ state_worksheets })}
          />
          <PostpartumCourseConfigEditor
            value={draft.postpartum_course}
            onChange={(postpartum_course) => update({ postpartum_course })}
          />
          <Field>
            <FieldLabel htmlFor="stillbirth-policy">Stillbirth record policy</FieldLabel>
            <Select
              value={draft.birth_identity_policy?.stillbirth_record_policy ?? 'UNCONFIGURED'}
              onValueChange={(value) =>
                update({
                  birth_identity_policy:
                    value === 'UNCONFIGURED'
                      ? undefined
                      : { stillbirth_record_policy: value as 'FETAL_RECORD' | 'PATIENT_CHART' },
                })
              }>
              <SelectTrigger id="stillbirth-policy" className="min-h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="UNCONFIGURED">Not configured — retain fetal record</SelectItem>
                  <SelectItem value="FETAL_RECORD">Retain fetal record</SelectItem>
                  <SelectItem value="PATIENT_CHART">Create a separately identified patient chart</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Review this recordkeeping policy for the practice’s jurisdictions and credentials. This setting does not
              grant clinical permissions.
            </p>
          </Field>
          <BirthTimerConfigEditor value={draft.birth_rules} onChange={(birth_rules) => update({ birth_rules })} />
          <NewbornGrowthConfigEditor
            value={draft.newborn_growth}
            onChange={(newborn_growth) => update({ newborn_growth })}
          />
          <NewbornScreeningConfigEditor
            value={draft.newborn_screening}
            onChange={(newborn_screening) => update({ newborn_screening })}
          />
          <BirthRuleConfigEditor value={draft.birth_rules} onChange={(birth_rules) => update({ birth_rules })} />
          <BirthDischargeConfigEditor
            value={draft.birth_discharge}
            onChange={(birth_discharge) => update({ birth_discharge })}
          />
          <Field>
            <FieldLabel htmlFor="offline-hours">Offline authorization duration (hours)</FieldLabel>
            <Input
              id="offline-hours"
              type="number"
              min={1}
              max={168}
              step={1}
              value={draft.offline_authorization_hours ?? 24}
              onChange={(e) => update({ offline_authorization_hours: Number(e.target.value) })}
            />
            <p className="text-sm text-muted-foreground">
              After expiry, cached records and pending changes remain available; new offline observations require online
              renewal.
            </p>
          </Field>
          <Field>
            <FieldLabel htmlFor="late-entry-minutes">Late-entry interval (minutes)</FieldLabel>
            <Input
              id="late-entry-minutes"
              type="number"
              min={1}
              max={1440}
              required
              value={Number.isFinite(draft.late_entry_minutes) ? draft.late_entry_minutes : ''}
              onChange={(e) =>
                update({ late_entry_minutes: e.target.value === '' ? Number.NaN : Number(e.target.value) })
              }
            />
          </Field>
          <section className="flex flex-col gap-3">
            <h3>Visible columns and order</h3>
            <p>Maternal and fetal columns remain grouped by subject.</p>
            {columns.map((key, index) => (
              <div key={key} className="flex flex-wrap items-center gap-4">
                <Checkbox
                  id={`column-${key}`}
                  checked={!draft.hidden_columns.includes(key)}
                  onCheckedChange={(value) =>
                    update({
                      hidden_columns: value
                        ? draft.hidden_columns.filter((k) => k !== key)
                        : [...draft.hidden_columns, key],
                    })
                  }
                />
                <label htmlFor={`column-${key}`}>
                  {displayedDefinition.columns.find((c) => c.key === key)?.label ?? key}
                </label>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!index}
                  aria-label={`Move ${key} up`}
                  onClick={() => move(index, -1)}>
                  Up
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={index === columns.length - 1}
                  aria-label={`Move ${key} down`}
                  onClick={() => move(index, 1)}>
                  Down
                </Button>
              </div>
            ))}
          </section>
          <section className="flex flex-col gap-4">
            <h3>Checklist windows</h3>
            <p>
              Changing an item’s label, window, linked test, or handout creates a new assessment. Existing completion
              records remain in history.
            </p>
            {draft.checklist.map((item, index) => (
              <div key={item.id} className="grid gap-3 rounded border p-4 sm:grid-cols-2">
                <Field>
                  <FieldLabel htmlFor={`item-${item.id}`}>Checklist item</FieldLabel>
                  <Input
                    id={`item-${item.id}`}
                    required
                    maxLength={200}
                    value={item.label}
                    onChange={(e) =>
                      update({
                        checklist: draft.checklist.map((v, i) => (i === index ? { ...v, label: e.target.value } : v)),
                      })
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor={`handout-${item.id}`}>Patient handout (HTTPS URL, optional)</FieldLabel>
                  <Input
                    id={`handout-${item.id}`}
                    type="url"
                    pattern="https://.*"
                    maxLength={2048}
                    value={item.handout_url ?? ''}
                    onChange={(e) =>
                      update({
                        checklist: draft.checklist.map((v, i) =>
                          i === index ? { ...v, handout_url: e.target.value || null } : v,
                        ),
                      })
                    }
                  />
                </Field>
                {(['min_ga_days', 'max_ga_days'] as const).map((key) => (
                  <Field key={key}>
                    <FieldLabel htmlFor={`${item.id}-${key}`}>
                      {key === 'min_ga_days' ? 'Window starts (GA days)' : 'Window ends (GA days)'}
                    </FieldLabel>
                    <Input
                      id={`${item.id}-${key}`}
                      type="number"
                      min={key === 'max_ga_days' && Number.isFinite(item.min_ga_days) ? item.min_ga_days : 0}
                      max={350}
                      required
                      value={Number.isFinite(item[key]) ? item[key] : ''}
                      onChange={(e) =>
                        update({
                          checklist: draft.checklist.map((v, i) =>
                            i === index
                              ? { ...v, [key]: e.target.value === '' ? Number.NaN : Number(e.target.value) }
                              : v,
                          ),
                        })
                      }
                    />
                  </Field>
                ))}
                <Field>
                  <FieldLabel htmlFor={`${item.id}-consent`}>Related test or procedure (optional)</FieldLabel>
                  <Input
                    id={`${item.id}-consent`}
                    maxLength={200}
                    value={item.consent_test ?? ''}
                    onChange={(e) =>
                      update({
                        checklist: draft.checklist.map((v, i) =>
                          i === index ? { ...v, consent_test: e.target.value || null } : v,
                        ),
                      })
                    }
                  />
                </Field>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => update({ checklist: draft.checklist.filter((_, i) => i !== index) })}>
                  Remove checklist item
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              disabled={draft.checklist.length >= 100}
              onClick={() =>
                update({
                  checklist: [
                    ...draft.checklist,
                    { id: crypto.randomUUID(), label: '', min_ga_days: Number.NaN, max_ga_days: Number.NaN },
                  ],
                })
              }>
              Add checklist item
            </Button>
          </section>
          <section className="flex flex-col gap-4">
            <h3>Numeric observation rules</h3>
            {draft.rules.map((item, index) => (
              <div key={item.id} className="grid gap-3 rounded border p-4 sm:grid-cols-2">
                <Choice
                  label={`Rule ${index + 1} observation field`}
                  value={`${item.event_type}:${item.path}`}
                  choices={numericSources}
                  onChange={(value) => {
                    const [event_type, path] = value.split(':');
                    rule(index, { event_type, path });
                  }}
                />
                <Choice
                  label={`Rule ${index + 1} comparison`}
                  value={item.operator}
                  choices={[
                    { value: 'lt', label: 'Less than' },
                    { value: 'lte', label: 'At most' },
                    { value: 'gt', label: 'Greater than' },
                    { value: 'gte', label: 'At least' },
                    { value: 'eq', label: 'Equal to' },
                  ]}
                  onChange={(value) => rule(index, { operator: value as PracticeRule['operator'] })}
                />
                <Field>
                  <FieldLabel htmlFor={`${item.id}-threshold`}>
                    Threshold — {sourceNames[`${item.event_type}:${item.path}`] ?? item.path}
                  </FieldLabel>
                  <Input
                    id={`${item.id}-threshold`}
                    type="number"
                    step="any"
                    required
                    value={Number.isFinite(item.threshold) ? item.threshold : ''}
                    onChange={(e) =>
                      rule(index, { threshold: e.target.value === '' ? Number.NaN : Number(e.target.value) })
                    }
                  />
                </Field>
                <Choice
                  label={`Rule ${index + 1} severity`}
                  value={item.severity}
                  choices={choices(['info', 'warn', 'critical'])}
                  onChange={(value) => rule(index, { severity: value as PracticeRule['severity'] })}
                />
                <Field>
                  <FieldLabel htmlFor={`${item.id}-message`}>Clinical message</FieldLabel>
                  <Input
                    id={`${item.id}-message`}
                    required
                    maxLength={500}
                    value={item.message}
                    onChange={(e) => rule(index, { message: e.target.value })}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor={`${item.id}-citation`}>Clinical source / citation</FieldLabel>
                  <Input
                    id={`${item.id}-citation`}
                    required
                    maxLength={1000}
                    value={item.citation}
                    onChange={(e) => rule(index, { citation: e.target.value })}
                  />
                </Field>
                <Choice
                  label={`Rule ${index + 1} evidence confidence`}
                  value={item.confidence}
                  choices={choices(['Certain', 'Likely', 'Guessing'])}
                  onChange={(value) => rule(index, { confidence: value as PracticeRule['confidence'] })}
                />
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`${item.id}-enabled`}
                    checked={item.enabled}
                    onCheckedChange={(value) => rule(index, { enabled: value === true })}
                  />
                  <label htmlFor={`${item.id}-enabled`}>Enable after clinical review</label>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => update({ rules: draft.rules.filter((_, i) => i !== index) })}>
                  Remove rule
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              disabled={draft.rules.length >= 100}
              onClick={() =>
                update({
                  rules: [
                    ...draft.rules,
                    {
                      id: crypto.randomUUID(),
                      event_type: 'VITALS.BP',
                      path: 'systolic',
                      operator: 'gte',
                      threshold: Number.NaN,
                      severity: 'warn',
                      message: '',
                      citation: '',
                      confidence: 'Guessing',
                      enabled: false,
                    },
                  ],
                })
              }>
              Add rule
            </Button>
          </section>
          <EventPolicyEditor
            value={draft.event_policies ?? []}
            onChange={(event_policies) => update({ event_policies })}
          />
          <CustomObservationEditor
            definitions={draft.custom_observations ?? []}
            published={baseline.custom_observations ?? []}
            onChange={(custom_observations) => {
              const keys = new Set(
                withCustomColumns(prenatalDefinition, custom_observations).columns.map((c) => c.key),
              );
              update({
                custom_observations,
                column_order: draft.column_order.filter((key) => keys.has(key)),
                hidden_columns: draft.hidden_columns.filter((key) => keys.has(key)),
                column_labels: Object.fromEntries(
                  Object.entries(draft.column_labels ?? {}).filter(([key]) => keys.has(key)),
                ),
              });
            }}
          />
          <ColumnLabelEditor
            columns={displayedDefinition.columns}
            labels={draft.column_labels ?? {}}
            onChange={(column_labels) => update({ column_labels })}
          />
          <ClinicalReferenceEditor
            value={draft.clinical_references}
            enabledAlerts={draft.prenatal_alerts ?? []}
            onChange={(clinical_references, prenatal_alerts) => update({ clinical_references, prenatal_alerts })}
          />
          <RulePreview configuration={draft} />
          <Field>
            <FieldLabel htmlFor="configuration-review-note">Clinical review note</FieldLabel>
            <Textarea
              id="configuration-review-note"
              required
              maxLength={2000}
              value={reviewNote}
              onChange={(e) => {
                setReviewNote(e.target.value);
                setReviewed(false);
              }}
            />
          </Field>
          <div className="flex items-center gap-3">
            <Checkbox
              id="configuration-reviewed"
              checked={reviewed}
              onCheckedChange={(value) => setReviewed(value === true)}
            />
            <label htmlFor="configuration-reviewed">
              I reviewed this configuration and its clinical sources for this practice.
            </label>
          </div>
          <Button type="submit" disabled={!reviewed || !reviewNote.trim()}>
            Publish reviewed configuration
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setDraft(structuredClone(configuration));
              setBaseline(structuredClone(configuration));
              setReviewed(false);
              setReviewNote('');
              setMessage('Latest loaded version restored; local configuration edits discarded.');
            }}>
            Discard edits and use latest loaded version
          </Button>
        </fieldset>
        {message && <p role="status">{message}</p>}
      </form>
    </details>
  );
}
